import { pollColorPalette } from "../utils/pollColorPalette";

interface BaseVisualizationProps {
  optionId: string | undefined;
  counts: number[];
  bucketLabels: string[];
  rankColors: string[];
  overflowColor: string;
  averageRank: number;
  showAverageMarker?: boolean;
}

export interface HistogramProps extends BaseVisualizationProps {
  userRank: number | undefined;
  userRankBucketIndex: number | null;
  averagePosition: number;
  displayRankCount: number;
  maxRank: number;
  responseCount: number;
  maxDensity: number;
}

export const RankingHistogram = ({
  optionId,
  counts,
  bucketLabels,
  rankColors,
  overflowColor,
  userRank,
  userRankBucketIndex,
  averageRank,
  averagePosition,
  displayRankCount,
  maxRank,
  responseCount,
  maxDensity,
  showAverageMarker = true,
}: HistogramProps) => {
  const overflowSpan = Math.max(0, maxRank - displayRankCount);
  const hasOverflow = counts.length > displayRankCount;
  const lowRankCount = hasOverflow ? counts[displayRankCount] ?? 0 : 0;
  const unrankedCount = hasOverflow ? counts[displayRankCount + 1] ?? 0 : 0;

  const buckets = counts.slice(0, displayRankCount).map((count, rankIndex) => {
    const span = 1;
    return {
      key: `${optionId}-histogram-${rankIndex}`,
      label: bucketLabels[rankIndex],
      count,
      color: rankColors[rankIndex] || overflowColor,
      span,
      density: span > 0 ? count / span : 0,
      isUserRank: userRankBucketIndex === rankIndex,
      percentage: responseCount === 0 ? 0 : (count / responseCount) * 100,
    };
  });

  const overflowBucketSpan = hasOverflow ? Math.max(1, overflowSpan) : 0;
  const overflowTotal = lowRankCount + unrankedCount;
  const overflowPercentage = responseCount === 0 ? 0 : (overflowTotal / responseCount) * 100;
  const lowRankPercentage = responseCount === 0 ? 0 : (lowRankCount / responseCount) * 100;
  const unrankedPercentage = responseCount === 0 ? 0 : (unrankedCount / responseCount) * 100;
  const overflowShare = lowRankPercentage + unrankedPercentage;
  const lowRankShare = overflowShare === 0 ? 0 : (lowRankPercentage / overflowShare) * 100;
  const unrankedShare = overflowShare === 0 ? 0 : (unrankedPercentage / overflowShare) * 100;
  const overflowLabel =
    maxRank > displayRankCount ? `Ranks ${displayRankCount + 1}-${maxRank} & Unranked` : "Additional ranks";
  const userOverflowBucket = userRankBucketIndex !== null && userRankBucketIndex >= displayRankCount;
  const isUserLowRankBucket = userRankBucketIndex === displayRankCount;
  const isUserUnrankedBucket = userRankBucketIndex === displayRankCount + 1;

  // Calculate max density (count per span) for normalizing bar heights so column area reflects vote totals
  const overflowDensity = overflowBucketSpan > 0 ? overflowTotal / overflowBucketSpan : 0;
  const computedMaxDensity = Math.max(...buckets.map((b) => b.density), overflowDensity, 0);
  const normalizedMaxDensity = maxDensity > 0 ? maxDensity : computedMaxDensity;

  const normalizedOverflowHeight =
    normalizedMaxDensity > 0
      ? ((overflowBucketSpan > 0 ? overflowTotal / overflowBucketSpan : 0) / normalizedMaxDensity) * 100
      : 0;
  const overflowBarHeight = Math.max(normalizedOverflowHeight, overflowTotal > 0 ? 6 : 0);
  const userOverflowShare = isUserLowRankBucket ? lowRankShare : isUserUnrankedBucket ? unrankedShare : 0;
  const userOverflowSegmentHeight = (overflowBarHeight * userOverflowShare) / 100;
  const showOverflowLabelAbove =
    userOverflowBucket && !!userRank && (userOverflowSegmentHeight < 20 || isUserLowRankBucket);
  const showOverflowLabelInside = userOverflowBucket && !!userRank && !showOverflowLabelAbove;
  const markers: {
    key: string;
    label: string;
    position: number;
    color: string;
    labelBg: string;
    labelText: string;
  }[] = [];

  if (showAverageMarker && averageRank) {
    markers.push({
      key: "average",
      label: `Avg ${averageRank.toFixed(1)}`,
      position: averagePosition,
      color: pollColorPalette.averageIndicator,
      labelBg: "#ffffff",
      labelText: pollColorPalette.averageIndicator,
    });
  }

  const clampPosition = (position: number) => Math.min(100, Math.max(0, position));
  const clampedMarkerPositions = markers.map((marker) => clampPosition(marker.position));
  const markerOverlapThreshold = 4;
  const markerBaseTop = 6;
  const markerStaggerOffset = 22;
  const markerTopOffsets = clampedMarkerPositions.map((position, index) => {
    const overlaps = clampedMarkerPositions
      .slice(0, index)
      .some((prev) => Math.abs(prev - position) < markerOverlapThreshold);
    return overlaps ? markerBaseTop + markerStaggerOffset : markerBaseTop;
  });

  return (
    <div className="relative w-full space-y-3">
      <div className="relative flex items-end gap-1 h-28 px-2 pt-4 pb-4 overflow-hidden">
        {buckets.map((bucket) => {
          const normalizedHeight = normalizedMaxDensity > 0 ? (bucket.density / normalizedMaxDensity) * 100 : 0;
          const barHeight = Math.max(normalizedHeight, bucket.count > 0 ? 6 : 0);
          const showUserLabelAbove = bucket.isUserRank && !!userRank && barHeight < 20;
          const showUserLabelInside = bucket.isUserRank && !!userRank && !showUserLabelAbove;
          return (
            <div
              key={bucket.key}
              className="flex flex-col items-center gap-1 min-w-[40px] h-full"
              style={{ flexGrow: bucket.span, flexBasis: 0 }}
            >
              <div className="relative w-full h-full flex items-end">
                <div
                  className="relative w-full overflow-hidden rounded-sm"
                  style={{ height: `${Math.min(100, barHeight)}%` }}
                  title={`${bucket.label}: ${bucket.count} response${bucket.count === 1 ? "" : "s"} (${bucket.percentage.toFixed(1)}%)`}
                >
                  <div className="absolute inset-0" style={{ backgroundColor: bucket.color }} />
                  {bucket.isUserRank && (
                    <div className="absolute inset-0 border-[2px] border-black rounded-sm pointer-events-none" />
                  )}
                  {showUserLabelInside && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-black">
                      <span className="px-1.5 py-0.5 rounded bg-white/85">{userRank}</span>
                    </div>
                  )}
                </div>
                {showUserLabelAbove ? (
                  <div className="absolute -top-11 left-1/2 -translate-x-1/2 px-2 py-0.5 text-[10px] font-semibold rounded border bg-background shadow-sm">
                    {userRank}
                  </div>
                ) : null}
              </div>
              <span className="text-[10px] text-center text-muted-foreground leading-tight">{bucket.label}</span>
            </div>
          );
        })}

        {hasOverflow ? (
          <div
            className="flex flex-col items-center gap-1 min-w-[56px] h-full"
            style={{ flexGrow: overflowBucketSpan, flexBasis: 0 }}
          >
            <div className="relative w-full h-full flex items-end">
              <div
                className="relative w-full overflow-hidden rounded-sm"
                style={{ height: `${Math.min(100, overflowBarHeight)}%` }}
                title={`${overflowLabel}: ${overflowTotal} response${overflowTotal === 1 ? "" : "s"} (${overflowPercentage.toFixed(1)}%)`}
              >
                <div className="absolute inset-0 flex flex-col-reverse">
                  {unrankedShare > 0 && (
                    <div
                      className="relative w-full"
                      style={{
                        height: `${unrankedShare}%`,
                        backgroundColor: rankColors[displayRankCount + 1] || overflowColor,
                      }}
                    >
                      {isUserUnrankedBucket && (
                        <div className="pointer-events-none absolute inset-0 border-[2px] border-black rounded-sm" />
                      )}
                      {showOverflowLabelInside && isUserUnrankedBucket && (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-black">
                          <span className="px-1.5 py-0.5 rounded bg-white/85">{userRank}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {lowRankShare > 0 && (
                    <div
                      className="relative w-full"
                      style={{
                        height: `${lowRankShare}%`,
                        backgroundColor: rankColors[displayRankCount] || overflowColor,
                      }}
                    >
                      {isUserLowRankBucket && (
                        <div className="pointer-events-none absolute inset-0 border-[2px] border-black rounded-sm" />
                      )}
                      {showOverflowLabelInside && isUserLowRankBucket && (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-black">
                          <span className="px-1.5 py-0.5 rounded bg-white/85">{userRank}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {showOverflowLabelAbove && (
                <div className="absolute -top-11 left-1/2 -translate-x-1/2 px-2 py-0.5 text-[10px] font-semibold rounded border bg-background shadow-sm">
                  {userRank}
                </div>
              )}
            </div>
            <span className="text-[10px] text-center text-muted-foreground leading-tight">
              {overflowLabel}
            </span>
          </div>
        ) : null}

        {markers.length > 0 ? (
          <div className="pointer-events-none absolute inset-0" aria-hidden="true">
            {markers.map((marker) => (
              <div
                key={`${optionId}-${marker.key}-line`}
                className="absolute top-2 bottom-8 w-[3px]"
                style={{
                  left: `${clampPosition(marker.position)}%`,
                  backgroundColor: marker.color,
                }}
              />
            ))}
          </div>
        ) : null}

        {markers.map((marker, index) => (
          <div
            key={`${optionId}-${marker.key}-label`}
            className="pointer-events-none absolute text-[10px] font-semibold px-2 py-0.5 rounded shadow-sm border"
            style={{
              left: `${clampedMarkerPositions[index]}%`,
              transform: "translateX(-50%)",
              backgroundColor: marker.labelBg,
              color: marker.labelText,
              borderColor: marker.color,
              top: `${markerTopOffsets[index]}px`,
            }}
          >
            {marker.label}
          </div>
        ))}
      </div>
    </div>
  );
};
